import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';

import {
    ActivityLayout,
    ActivityNavigation,
    ActivityTopBar,
    FloorViewEditor,
    KeyParameterEditor,
    ModuleAddItem,
    ModuleRenderer,
    ModuleReorderGroup,
    ModuleSelectionModal,
    NameModal,
    Scrubber,
    SwingFoundations,
    SwingVideos,
} from '@blocks';
import { useLayoutEditing, useModuleOrdering, useToggle } from '@hooks';
import { activityIdAtom, currentPositionNumberAtom, layoutAtom, studentIdAtom } from '@store';
import { Box } from '@ui';

import { Navigation } from '../components/Navigation';
import { SwingThreadForm } from '../components/SwingThreadForm/SwingThreadForm';
import { LINKS } from '../utils/routes';

import * as css from './screens.css';

export function SwingDetailsScreen() {
    const navigate = useNavigate();

    const [showSwingThreadForm, toggleSwingThreadForm] = useToggle(false);

    const activityId = useAtomValue(activityIdAtom);
    const studentId = useAtomValue(studentIdAtom);

    const currentPosition = useAtomValue(currentPositionNumberAtom);

    const layout = useAtomValue(layoutAtom);

    const {
        isEditing,
        isEditingFloor,
        isEditingKeyParam,
        createLayoutCopy,
        handleCreateLayoutCopy,
        toggleCreateLayoutCopy,
        handleEditingLayout,
        handleEditingFloorLayout,
        handleEditingKeyParam,
        handleNavigateToEditor,
    } = useLayoutEditing();
    const {
        kioskModules,
        kioskModuleIds,
        handleReorder,
        handleRemove,
        handleAdd,
        isLoading,
        setAddItemIndex,
        addItemIndex,
    } = useModuleOrdering();

    return (
        <>
            <ActivityLayout getActivities={LINKS.getOverview} hasConnection>
                <Navigation isActivity isSwing>
                    <ActivityTopBar
                        onCreateNote={toggleSwingThreadForm}
                        isEditingLayout={isEditing}
                        isEditingFloor={isEditingFloor}
                        onEditLayout={handleEditingLayout}
                        onEditFloor={handleEditingFloorLayout}
                    />
                </Navigation>
                <div className={css.layout}>
                    <ActivityNavigation
                        onSelectActive={(swingId) => navigate(LINKS.getFullActivity(activityId, swingId))}
                        onDeleteActivity={() => navigate(LINKS.getOverview(studentId))}
                        onEditKeyParameter={handleEditingKeyParam}
                        onNavigateToLiveAnalysis={() => navigate(LINKS.getLiveAnalysis())}
                        onNavigateToNewSwing={() => navigate(LINKS.getActivity())}
                        isNewSwing
                    />
                    <Box className={css.videoWrapper}>
                        <Box className={css.video}>
                            <SwingVideos />
                        </Box>
                        <div className={css.columnLayout}>
                            <SwingFoundations
                                isEditing={isEditing}
                                navigateToEditor={() => handleNavigateToEditor(() => navigate(LINKS.getParameters()))}
                                videoComponent={<SwingVideos />}
                            />
                            <ModuleReorderGroup
                                isEditing={isEditing}
                                ids={kioskModuleIds}
                                onReorder={handleReorder}
                                isLoading={isLoading}
                            >
                                <ModuleRenderer
                                    modules={kioskModules}
                                    isEditing={isEditing}
                                    handleRemove={handleRemove}
                                    setAddItemIndex={setAddItemIndex}
                                />
                                <ModuleAddItem
                                    isEditing={isEditing}
                                    onAdd={() => setAddItemIndex(kioskModules.length)}
                                />
                            </ModuleReorderGroup>
                        </div>
                    </Box>
                </div>
                <Scrubber />
            </ActivityLayout>
            {showSwingThreadForm && (
                <SwingThreadForm activePosition={currentPosition} onClose={toggleSwingThreadForm} />
            )}
            {createLayoutCopy && (
                <NameModal
                    header="Copy layout in order to edit?"
                    label="Layout Name"
                    value={layout?.name + ' (copy)'}
                    onDismiss={toggleCreateLayoutCopy}
                    onSubmit={handleCreateLayoutCopy}
                />
            )}
            {addItemIndex !== null && (
                <ModuleSelectionModal
                    onDismiss={() => setAddItemIndex(null)}
                    onSelect={handleAdd}
                    selectedIds={kioskModuleIds}
                />
            )}
            {isEditingFloor && <FloorViewEditor onDismiss={handleEditingFloorLayout} />}
            {isEditingKeyParam && <KeyParameterEditor onClose={handleEditingKeyParam} />}
        </>
    );
}
